import { Paperclip, Send, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSendMessage } from '@services/chat/mutations/useSendMessage'

export function ChatComposer({ chatId }: { chatId: string }) {
  const { t } = useTranslation('chat')
  const sendMessage = useSendMessage()
  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const fileInput = useRef<HTMLInputElement>(null)

  const canSend = (message.trim() || files.length > 0) && !sendMessage.isPending

  const handleSend = () => {
    if (!canSend) return
    sendMessage.mutate({
      id: chatId,
      body: { message, attachments: files },
      params: {
        onSuccess: () => {
          setMessage('')
          setFiles([])
        }
      }
    })
  }

  return (
    <div className="shrink-0 border-t border-ink/[0.06] bg-white p-3">
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((file, i) => (
            <span
              key={`${file.name}-${i}`}
              className="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-ink/70"
            >
              <Paperclip className="h-3 w-3" />
              <span className="max-w-[120px] truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => setFiles((f) => f.filter((_, idx) => idx !== i))}
                aria-label="Remove file"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          rows={1}
          placeholder={t('form.common.message_placeholder')}
          className="max-h-28 min-h-[2.75rem] flex-1 resize-none rounded-2xl border border-input bg-white px-4 py-3 text-sm text-ink placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none"
        />

        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          aria-label="Attach file"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink/60 transition-colors hover:bg-ink/[0.05] hover:text-ink"
        >
          <Paperclip className="h-5 w-5" />
        </button>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            const picked = e.target.files ? Array.from(e.target.files) : []
            if (picked.length) setFiles((f) => [...f, ...picked])
            e.target.value = ''
          }}
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          aria-label={t('form.common.send')}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
